import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";

const BADGE_STYLE = {
  confirmed: "bg-green-50 text-green-700 border-green-300",
  different: "bg-amber-50 text-amber-700 border-amber-300",
  not_tested: "bg-slate-100 text-slate-500 border-slate-300",
  failed: "bg-red-50 text-red-700 border-red-300",
};
const BADGE_ICON = { confirmed: "✓", different: "⚠", not_tested: "○", failed: "✕" };

const VERDICTS = {
  SUCCESS_WITH_RUNTIME_INJECTION: {
    title: "Correction appliquée via Pont Emergent",
    cls: "bg-blue-50 border-blue-200 text-blue-900", icon: "✅",
  },
  SOURCE_NATIVE: {
    title: "Correction SEO native ✓",
    cls: "bg-green-50 border-green-200 text-green-900", icon: "✅",
  },
  SAVED_NOT_ACTIVE: {
    title: "Correction enregistrée mais non active sur le site",
    cls: "bg-red-50 border-red-200 text-red-900", icon: "⚠️",
  },
  BRIDGE_UNREACHABLE: {
    title: "Pont Emergent détecté mais indisponible",
    cls: "bg-red-50 border-red-200 text-red-900", icon: "⚠️",
  },
  OVERRIDE_MISSING: {
    title: "Aucun override actif pour cette page",
    cls: "bg-red-50 border-red-200 text-red-900", icon: "⚠️",
  },
  OVERRIDE_MISMATCH: {
    title: "Le pont sert une autre valeur que celle attendue",
    cls: "bg-amber-50 border-amber-200 text-amber-900", icon: "⚠️",
  },
};

const Check = ({ ok, warn, children }) => (
  <li className="flex items-start gap-1.5">
    <span className={ok ? "text-green-600" : warn ? "text-slate-500" : "text-red-600"}>
      {ok ? "✓" : warn ? (warn === "info" ? "ℹ" : "○") : "✕"}
    </span>
    <span>{children}</span>
  </li>
);

export function BridgeDiagnosticPanel({ fixId }) {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(false);

  const run = useCallback(async () => {
    if (!fixId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/seo-fix/bridge-diagnostic/${fixId}`, {
        api_base: process.env.REACT_APP_BACKEND_URL,
      });
      setDiag(data);
    } catch {
      setDiag(null);
    } finally {
      setLoading(false);
    }
  }, [fixId]);

  useEffect(() => { run(); }, [run]);

  if (loading && !diag) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-testid="bridge-diag-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Diagnostic du Pont Emergent en cours…
      </div>
    );
  }
  if (!diag) return null;

  const v = VERDICTS[diag.verdict] || VERDICTS.OVERRIDE_MISMATCH;
  const b = diag.bridge || {};
  const src = diag.source_check || {};
  const rt = diag.runtime_check || {};
  const badges = diag.badges || {};

  return (
    <div className={`mt-3 border rounded-md px-3 py-3 text-sm ${v.cls}`} data-testid="bridge-diagnostic-panel">
      <div className="font-semibold" data-testid="bridge-diag-verdict">{v.icon} {v.title}</div>

      {/* Badges 4 niveaux */}
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="bridge-diag-badges">
        {[["HTML SOURCE", badges.html_source], ["PONT", badges.bridge],
          ["RUNTIME", badges.runtime], ["GOOGLE", badges.google]].map(([label, state]) => (
          <span key={label}
            data-testid={`bridge-badge-${label.toLowerCase().replace(" ", "-")}`}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${BADGE_STYLE[state] || BADGE_STYLE.not_tested}`}>
            {BADGE_ICON[state] || "○"} {label}
          </span>
        ))}
      </div>

      {/* Checklist selon le niveau de preuve réel */}
      <ul className="mt-2 space-y-1 text-xs" data-testid="bridge-diag-checklist">
        <Check ok={b.override_saved && b.override_enabled}>Override enregistré</Check>
        <Check ok={b.operational} warn={!b.installed ? false : undefined}>
          Pont Emergent {b.operational ? "actif" : b.script_seen ? "détecté mais indisponible" : "non détecté sur le site"}
        </Check>
        <Check ok={b.override_servable}>
          {b.override_servable
            ? (b.servable_proof === "http"
              ? "Correction disponible au chargement de la page (vérifiée de bout en bout)"
              : "Correction servable (vérifiée en base — test HTTP non exécuté)")
            : "Correction non servie"}
        </Check>
        {src.confirmed
          ? <Check ok>Présente dans le HTML source</Check>
          : <Check warn="info">HTML source initial non modifié{src.status === "unavailable" ? " (page inaccessible pour vérification)" : ""}</Check>}
        {rt.status !== "not_tested" && (
          <Check ok={rt.status === "confirmed"}>
            {rt.status === "confirmed" ? "Valeur appliquée après chargement JavaScript (DOM rendu vérifié)" : "DOM rendu : valeur différente de celle attendue"}
          </Check>
        )}
        <Check warn>Google : validation à effectuer dans Search Console</Check>
      </ul>

      {diag.verdict === "SAVED_NOT_ACTIVE" && (
        <div className="mt-2 text-xs font-medium" data-testid="bridge-diag-install-cta">
          → Installez le Pont Emergent : onglet Corrections, panneau « Pont Emergent », bouton « Instructions d'installation + script ».
        </div>
      )}
      {diag.robots_warning && (
        <div className="mt-2 text-xs font-medium text-red-700" data-testid="bridge-diag-robots-warning">{diag.robots_warning}</div>
      )}
      {diag.runtime_canonical_only && (
        <div className="mt-2 text-xs text-amber-800" data-testid="bridge-diag-canonical-warning">
          ⚠️ Canonical servi uniquement après JavaScript. {diag.canonical_recommendation}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3">
        <button onClick={run} disabled={loading} data-testid="bridge-diag-rerun"
          className="inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Relancer le diagnostic
        </button>
        <button onClick={() => setDetails(!details)} data-testid="bridge-diag-details-toggle"
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          {details ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Voir les détails techniques
        </button>
      </div>

      {details && (
        <div className="mt-2 text-[11px] bg-white/70 border border-slate-200 rounded-md p-2.5 space-y-1 text-slate-700" data-testid="bridge-diag-details">
          <div><span className="font-semibold">SOURCE ({diag.category}) :</span> {src.value || "(absente)"}</div>
          {rt.status !== "not_tested" && <div><span className="font-semibold">RUNTIME (DOM rendu) :</span> {rt.value || "(absente)"}</div>}
          <div><span className="font-semibold">ATTENDUE :</span> {diag.expected_value}</div>
          <div><span className="font-semibold">Endpoint public :</span> {b.endpoint?.detail || "non testé"}</div>
          {b.last_fetch_at && <div><span className="font-semibold">Dernier contact du script :</span> {new Date(b.last_fetch_at).toLocaleString("fr-FR")}</div>}
          {src.robots && <div><span className="font-semibold">Robots (HTML source) :</span> {src.robots}</div>}
          <div className="text-slate-500 pt-1 border-t border-slate-100">
            {diag.google?.message}
          </div>
        </div>
      )}
    </div>
  );
}
